import React, { useState, useRef, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { CompactListSkeleton, CarouselSkeleton } from "./SkeletonLoaders";
import "./BottomSheet.css";

// Sheet states
export const SHEET_STATES = {
  COLLAPSED: "collapsed", // ~120px, shows carousel
  HALF: "half", // 50vh, shows compact list
  FULL: "full", // 100vh, shows detailed list
};

// Minimum drag distance to trigger state change
const DRAG_THRESHOLD = 50;

const BottomSheet = ({
  children,
  restaurants,
  selectedRestaurantId,
  onRestaurantSelect,
  onStateChange,
  state: controlledState,
  initialState = SHEET_STATES.COLLAPSED,
  hideHeader = false,
  renderCollapsed,
  renderHalf,
  renderFull,
  onFollowCity,
  onSaveSearch,
}) => {
  const [internalState, setInternalState] = useState(initialState);

  // Use controlled state if provided, otherwise use internal state
  const sheetState = controlledState !== undefined ? controlledState : internalState;
  const setSheetState = controlledState !== undefined
    ? (newState) => onStateChange?.(newState)
    : setInternalState;
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  // Track which state we're previewing during drag (null = no preview)
  const [previewState, setPreviewState] = useState(null);

  const dragStartY = useRef(0);
  const contentRef = useRef(null);

  // Use refs to store latest values for event listeners (avoids stale closure)
  const isDraggingRef = useRef(false);
  const dragOffsetRef = useRef(0);
  const sheetStateRef = useRef(sheetState);
  const onStateChangeRef = useRef(onStateChange);
  const controlledStateRef = useRef(controlledState);

  // Keep refs in sync with state and props
  useEffect(function syncSheetStateRef() {
    sheetStateRef.current = sheetState;
  }, [sheetState]);

  useEffect(function syncIsDraggingRef() {
    isDraggingRef.current = isDragging;
  }, [isDragging]);

  useEffect(function syncDragOffsetRef() {
    dragOffsetRef.current = dragOffset;
  }, [dragOffset]);

  useEffect(function syncOnStateChangeRef() {
    onStateChangeRef.current = onStateChange;
  }, [onStateChange]);

  useEffect(function syncControlledStateRef() {
    controlledStateRef.current = controlledState;
  }, [controlledState]);

  // Handle drag start (touch or mouse)
  const handleDragStart = useCallback((clientY) => {
    setIsDragging(true);
    dragStartY.current = clientY;
    setDragOffset(0);
  }, []);

  // Handle drag move (touch or mouse)
  const handleDragMove = useCallback((clientY) => {
    if (!isDragging) return;
    const delta = clientY - dragStartY.current;
    setDragOffset(delta);

    // Determine preview state based on drag direction
    if (delta < -DRAG_THRESHOLD) {
      // Dragging up - preview expanded state
      if (sheetState === SHEET_STATES.COLLAPSED) {
        setPreviewState(SHEET_STATES.HALF);
      } else if (sheetState === SHEET_STATES.HALF) {
        setPreviewState(SHEET_STATES.FULL);
      } else {
        setPreviewState(null);
      }
    } else if (delta > DRAG_THRESHOLD) {
      // Dragging down - preview collapsed state
      if (sheetState === SHEET_STATES.FULL) {
        setPreviewState(SHEET_STATES.HALF);
      } else if (sheetState === SHEET_STATES.HALF) {
        setPreviewState(SHEET_STATES.COLLAPSED);
      } else {
        setPreviewState(null);
      }
    } else {
      setPreviewState(null);
    }
  }, [isDragging, sheetState]);

  // Handle drag end (touch or mouse)
  const handleDragEnd = useCallback(() => {
    if (!isDragging) return;

    setIsDragging(false);
    setPreviewState(null);

    // Determine new state based on drag direction and distance
    let newState = sheetState;

    if (dragOffset < -DRAG_THRESHOLD) {
      // Dragged up - expand
      if (sheetState === SHEET_STATES.COLLAPSED) {
        newState = SHEET_STATES.HALF;
      } else if (sheetState === SHEET_STATES.HALF) {
        newState = SHEET_STATES.FULL;
      }
    } else if (dragOffset > DRAG_THRESHOLD) {
      // Dragged down - collapse
      if (sheetState === SHEET_STATES.FULL) {
        newState = SHEET_STATES.HALF;
      } else if (sheetState === SHEET_STATES.HALF) {
        newState = SHEET_STATES.COLLAPSED;
      }
    }

    setDragOffset(0);

    if (newState !== sheetState) {
      setSheetState(newState);
      if (onStateChange) {
        onStateChange(newState);
      }
    }
  }, [isDragging, dragOffset, sheetState, onStateChange, setSheetState]);

  // Touch event handlers
  const handleTouchStart = useCallback((e) => {
    e.preventDefault();
    handleDragStart(e.touches[0].clientY);
  }, [handleDragStart]);

  const handleTouchMove = useCallback((e) => {
    e.preventDefault();
    handleDragMove(e.touches[0].clientY);
  }, [handleDragMove]);

  const handleTouchEnd = useCallback((e) => {
    e.preventDefault();
    handleDragEnd();
  }, [handleDragEnd]);

  // Mouse event handlers (for desktop testing)
  // Use refs for stable function references that can access latest state via other refs
  const globalMouseMoveRef = useRef((e) => {
    if (!isDraggingRef.current) return;
    const delta = e.clientY - dragStartY.current;
    setDragOffset(delta);
    dragOffsetRef.current = delta;

    // Determine preview state based on drag direction
    const currentSheetState = sheetStateRef.current;
    if (delta < -DRAG_THRESHOLD) {
      if (currentSheetState === SHEET_STATES.COLLAPSED) {
        setPreviewState(SHEET_STATES.HALF);
      } else if (currentSheetState === SHEET_STATES.HALF) {
        setPreviewState(SHEET_STATES.FULL);
      } else {
        setPreviewState(null);
      }
    } else if (delta > DRAG_THRESHOLD) {
      if (currentSheetState === SHEET_STATES.FULL) {
        setPreviewState(SHEET_STATES.HALF);
      } else if (currentSheetState === SHEET_STATES.HALF) {
        setPreviewState(SHEET_STATES.COLLAPSED);
      } else {
        setPreviewState(null);
      }
    } else {
      setPreviewState(null);
    }
  });

  const globalMouseUpRef = useRef(() => {
    if (!isDraggingRef.current) return;

    setIsDragging(false);
    isDraggingRef.current = false;
    setPreviewState(null);

    // Determine new state based on drag direction and distance
    const currentDragOffset = dragOffsetRef.current;
    const currentSheetState = sheetStateRef.current;
    let newState = currentSheetState;

    if (currentDragOffset < -DRAG_THRESHOLD) {
      // Dragged up - expand
      if (currentSheetState === SHEET_STATES.COLLAPSED) {
        newState = SHEET_STATES.HALF;
      } else if (currentSheetState === SHEET_STATES.HALF) {
        newState = SHEET_STATES.FULL;
      }
    } else if (currentDragOffset > DRAG_THRESHOLD) {
      // Dragged down - collapse
      if (currentSheetState === SHEET_STATES.FULL) {
        newState = SHEET_STATES.HALF;
      } else if (currentSheetState === SHEET_STATES.HALF) {
        newState = SHEET_STATES.COLLAPSED;
      }
    }

    setDragOffset(0);
    dragOffsetRef.current = 0;

    if (newState !== currentSheetState) {
      if (controlledStateRef.current !== undefined) {
        if (onStateChangeRef.current) {
          onStateChangeRef.current(newState);
        }
      } else {
        setInternalState(newState);
      }
    }

    // Remove global mouse listeners
    document.removeEventListener('mousemove', globalMouseMoveRef.current);
    document.removeEventListener('mouseup', globalMouseUpRef.current);
  });

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
    isDraggingRef.current = true;
    dragStartY.current = e.clientY;
    setDragOffset(0);
    dragOffsetRef.current = 0;
    // Add global mouse listeners
    document.addEventListener('mousemove', globalMouseMoveRef.current);
    document.addEventListener('mouseup', globalMouseUpRef.current);
  }, []);

  // Handle header tap to cycle states
  const handleHeaderTap = useCallback(() => {
    // Don't trigger on drag
    if (Math.abs(dragOffset) > 10) return;

    let newState;
    if (sheetState === SHEET_STATES.COLLAPSED) {
      newState = SHEET_STATES.HALF;
    } else if (sheetState === SHEET_STATES.HALF) {
      newState = SHEET_STATES.FULL;
    } else {
      newState = SHEET_STATES.COLLAPSED;
    }

    setSheetState(newState);
    if (onStateChange) {
      onStateChange(newState);
    }
  }, [sheetState, dragOffset, onStateChange]);

  // Collapse the sheet
  const collapse = useCallback(() => {
    setSheetState(SHEET_STATES.COLLAPSED);
    if (onStateChange) {
      onStateChange(SHEET_STATES.COLLAPSED);
    }
  }, [onStateChange]);

  // Render content based on current state
  const renderContent = () => {
    switch (sheetState) {
      case SHEET_STATES.COLLAPSED:
        return renderCollapsed ? renderCollapsed() : children;
      case SHEET_STATES.HALF:
        return renderHalf ? renderHalf() : children;
      case SHEET_STATES.FULL:
        return renderFull ? renderFull({ collapse }) : children;
      default:
        return children;
    }
  };

  // Get count text for header
  const getHeaderText = () => {
    const count = restaurants?.length || 0;
    if (sheetState === SHEET_STATES.COLLAPSED) {
      const selectedIndex = restaurants?.findIndex(
        (r) => r._id === selectedRestaurantId
      );
      if (selectedIndex >= 0) {
        return `${selectedIndex + 1} of ${count}`;
      }
    }
    return `${count} Restaurants`;
  };

  // Get skeleton content for preview state
  const renderSkeletonPreview = () => {
    if (!previewState) return null;

    switch (previewState) {
      case SHEET_STATES.COLLAPSED:
        return <CarouselSkeleton />;
      case SHEET_STATES.HALF:
      case SHEET_STATES.FULL:
        return <CompactListSkeleton count={previewState === SHEET_STATES.FULL ? 8 : 5} />;
      default:
        return null;
    }
  };

  // Determine which state class to use (preview or actual)
  const displayState = previewState || sheetState;

  return (
    <div
      className={`bottom-sheet bottom-sheet-${displayState} ${previewState ? 'bottom-sheet-previewing' : ''}`}
    >
      {/* Drag Handle - larger touch target */}
      <div
        className="bottom-sheet-drag-handle"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onClick={handleHeaderTap}
      >
        <div className="bottom-sheet-handle-bar" />
      </div>

      {/* Header - hidden when showing expanded card (which has its own header) */}
      {!hideHeader && (
        <div
          className="bottom-sheet-header"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleMouseDown}
          onClick={handleHeaderTap}
        >
          <span className="bottom-sheet-header-text">{getHeaderText()}</span>
          {restaurants?.length > 0 && (onSaveSearch || onFollowCity) && (
            <button
              type="button"
              className="follow-city-link follow-city-link-mobile"
              onClick={(e) => {
                e.stopPropagation();
                (onSaveSearch || onFollowCity)();
              }}
              onTouchEnd={(e) => {
                e.stopPropagation();
                e.preventDefault();
                (onSaveSearch || onFollowCity)();
              }}
            >
              <i className={`fas ${onSaveSearch ? "fa-bookmark" : "fa-bell"}`}></i>
              <span>{onSaveSearch ? "Save" : "Follow"}</span>
            </button>
          )}
          {sheetState === SHEET_STATES.FULL && (
            <button
              className="bottom-sheet-close-btn"
              onClick={(e) => {
                e.stopPropagation();
                collapse();
              }}
              onTouchEnd={(e) => {
                e.stopPropagation();
                e.preventDefault();
                collapse();
              }}
              aria-label="Close full view"
            >
              <i className="fas fa-times" />
            </button>
          )}
        </div>
      )}

      {/* Content */}
      <div className="bottom-sheet-content" ref={contentRef}>
        {previewState ? renderSkeletonPreview() : renderContent()}
      </div>
    </div>
  );
};

BottomSheet.propTypes = {
  children: PropTypes.node,
  restaurants: PropTypes.array,
  selectedRestaurantId: PropTypes.string,
  onRestaurantSelect: PropTypes.func,
  onStateChange: PropTypes.func,
  state: PropTypes.oneOf(Object.values(SHEET_STATES)),
  initialState: PropTypes.oneOf(Object.values(SHEET_STATES)),
  hideHeader: PropTypes.bool,
  renderCollapsed: PropTypes.func,
  renderHalf: PropTypes.func,
  renderFull: PropTypes.func,
};

export default BottomSheet;
